import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { siteData } from '../data/mock';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? 'py-3 bg-[#05070f] backdrop-blur-xl border-b border-[#d5b56b]/10 shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)]'
            : 'py-6 bg-transparent'
        }`}
      >
        <div className="container relative flex items-center justify-between gap-6">
          {/* Left nav */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-10 flex-1 justify-start">
            {siteData.navigation.slice(0, 3).map((n) => (
              <a key={n.label} href={n.href} className="nav-link">
                {n.label}
              </a>
            ))}
          </nav>

          {/* Logo — BRETECH wordmark, centered */}
          <a
            href="#home"
            className="group flex items-center select-none shrink-0"
          >
            <div
              className="relative overflow-hidden transition-all duration-500"
              style={{
                height: scrolled ? '48px' : '78px',
                width: scrolled ? '240px' : '320px',
              }}
            >
              <img
                src={siteData.company.logo}
                alt="Bretech Solutions Group"
                className="absolute left-1/2 -translate-x-1/2 transition-all duration-500"
                style={{
                  height: scrolled ? '190px' : '290px',
                  width: scrolled ? '190px' : '290px',
                  bottom: scrolled ? '-18px' : '-24px',
                  mixBlendMode: 'lighten',
                }}
                draggable="false"
              />
            </div>
          </a>

          {/* Right nav */}
          <nav className="hidden md:flex items-center gap-8 lg:gap-10 flex-1 justify-end">
            {siteData.navigation.slice(3).map((n) => (
              <a key={n.label} href={n.href} className="nav-link">
                {n.label}
              </a>
            ))}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen(true)}
            className="md:hidden absolute right-6 p-2 text-[#eef1f8]"
            aria-label="open menu"
          >
            <Menu size={22} />
          </button>
        </div>

        <style>{`
          .nav-link {
            position: relative;
            font-family: 'Inter', sans-serif;
            font-size: 12px;
            letter-spacing: 0.24em;
            text-transform: uppercase;
            color: #eef1f8;
            opacity: 0.7;
            transition: opacity 0.3s;
          }
          .nav-link:hover { opacity: 1; color: #d5b56b; }
          .nav-link::before {
            content: '— ';
            color: #d5b56b;
            margin-right: 4px;
            opacity: 0;
            transition: opacity 0.3s;
          }
          .nav-link:hover::before { opacity: 1; }
        `}</style>
      </motion.header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-[#05070f]/95 backdrop-blur-lg md:hidden"
          >
            <div className="container flex flex-col h-full pt-8">
              <div className="flex justify-end">
                <button onClick={() => setOpen(false)} className="p-2 text-[#eef1f8]">
                  <X size={24} />
                </button>
              </div>
              <nav className="flex flex-col gap-8 mt-16">
                {siteData.navigation.map((n, i) => (
                  <motion.a
                    key={n.label}
                    href={n.href}
                    onClick={() => setOpen(false)}
                    initial={{ x: 30, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.06 }}
                    className="font-serif text-4xl text-[#eef1f8]"
                  >
                    {n.label}
                  </motion.a>
                ))}
              </nav>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
